// First-person interaction (FP briefs + master brief §4 + arm-origin fix brief).
//
// Four pieces, one contract:
//   1. FP ARMS   — TWO-BONE arms whose shoulder roots are BOLTED to the viewport corners
//                  (+/-0.28, -0.22, -0.35 in camera space) and never move again. Idle and reach
//                  are the SAME solver with different targets, so the arm can never "teleport"
//                  to the contact point: only its joints rotate. The first build translated the
//                  whole hand+forearm assembly to the target, which is why the right arm appeared
//                  to grow out of the middle of the screen and cross the frame.
//   2. TARGETS   — every touch aims at a MEASURED world point: the dog's petPoint marker, the
//                  cyborg's handTip marker (itself IK-driven toward the clasp), or a shared meet
//                  point. Re-derived every frame, so a bobbing head or a rising paw is tracked.
//   3. PALM PAD  — the IK targets the WRIST such that the PALM PAD lands on the point: the goal
//                  is target minus the pad offset rotated by the palm's chosen orientation.
//   4. FENCE     — the touch must be physical AND the arm must be rooted. The probe measures
//                  palm-to-target during hold, which limb reached (in the subject's own body
//                  frame), and that both arm roots are still exactly at their anchors.
//
// Zero assets: every part comes from the same procedural toolkit as the city and the cyborgs.

import * as THREE from 'three';
import {
  proceduralHand, beveledBox, jointCap, place, placeParts, mirrorX,
} from './geometry/procedural.js';
import { dressBone, cloneParts, cached } from './geometry/rigs.js';
import { solveTwoBone } from './creatures.js';
import { MODE } from './config.js';

const ss = (t) => t * t * (3 - 2 * t);   // smoothstep

// Arm segment lengths and the immovable shoulder anchors, in CAMERA space.
const ARM_U = 0.30, ARM_L = 0.32;
const ANCHOR = { x: 0.28, y: -0.22, z: -0.35 };
// Palm pad in the hand's own frame: the middle of the palm plate, on the palm surface. This is
// the point that must land on whatever is being touched.
const PAD = new THREE.Vector3(0, -0.068, 0.016);
// Idle wrist targets, camera space. Chosen so the resting hands sit low in frame with the elbows
// tucked back toward their anchors; |target - anchor| = 0.40 m, comfortably inside the 0.62 m arm.
const IDLE_AT = { x: 0.20, y: -0.34, z: -0.72 };
// Palm orientations in CAMERA space, per action. The hand's own frame extends -Y from the wrist
// with the palm facing +Z, so Rx(+pi/2) points the fingers forward and turns the palm downward.
const PALM = {
  idle: new THREE.Euler(1.16, 0, 0),
  pet: new THREE.Euler(1.52, -0.04, 0),          // flat, palm down onto the head cowling
  shakeDog: new THREE.Euler(1.15, -0.06, 1.30),  // rolled thumb-up to take a paw
  shakeNpc: new THREE.Euler(1.05, -0.12, 1.34),  // rolled thumb-up, a hand's clasp
};

/** Upper arm / forearm / hand part lists. side +1 = the player's right. */
function fpArmParts(side, kind) {
  return cached(`fp.${kind}.${side}`, () => {
    const P = [];
    if (kind === 'upper') {
      P.push(...jointCap(0.048, { spherical: true, slot: 'trim' }));           // shoulder ball
      P.push(...placeParts(beveledBox(0.086, ARM_U * 0.84, 0.090, { bevel: 0.011, slot: 'mechDark' }),
        { pos: [0, -ARM_U * 0.47, 0] }));
      P.push(...placeParts(beveledBox(0.062, 0.05, 0.066, { bevel: 0.006, slot: 'trim' }),
        { pos: [0, -ARM_U * 0.88, 0] }));
    } else if (kind === 'fore') {
      P.push(...jointCap(0.040, { spherical: true, slot: 'trim' }));           // elbow ball
      P.push(...placeParts(beveledBox(0.070, ARM_L * 0.78, 0.074, { bevel: 0.009, slot: 'mechDark' }),
        { pos: [0, -ARM_L * 0.44, 0] }));
      P.push({ geo: place(new THREE.CylinderGeometry(0.027, 0.029, 0.032, 10),
        { pos: [0, -ARM_L + 0.016, 0] }), slot: 'chrome' });                   // wrist collar
    } else {
      // Native hand frame: wrist at origin, fingers down -Y, palm facing +Z. The left hand is the
      // mirrored chirality (mirrorX re-winds the triangles).
      const hand = proceduralHand(1.05, 'chrome');
      for (const p of hand) P.push({ geo: side === 1 ? p.geo.clone() : mirrorX(p.geo.clone()), slot: p.slot });
    }
    return P;
  });
}

export class Interactions {
  constructor(scene, camera, player, fauna, cyborgs, countFn, avatar) {
    // Children of the camera only render if the camera itself is in the graph.
    scene.add(camera);
    this.camera = camera;
    this.player = player;
    this.fauna = fauna;
    this.cyborgs = cyborgs;
    this.count = countFn;
    // In third person the PLAYER'S OWN RIG does the reaching -- same arm, same solver as the NPC.
    // The camera-attached FP arms are a first-person-only cosmetic layer and are hidden.
    this.avatar = avatar || null;
    // ONE PHYSICAL BODY. The avatar does the reaching in both views -- it is the thing with
    // shoulders, a spine and a crouch. `tp` only decides what the CAMERA shows; `body` decides
    // what actually touches, so the measured contact is never a number about an invisible thing.
    this.tp = MODE.view === 'tp' && !!this.avatar;
    this.body = !!this.avatar;

    // --- the two arms; roots pinned to the viewport corners for good -------------------------
    this.arms = [];
    for (const side of [1, -1]) {
      const root = new THREE.Group();
      root.position.set(side * ANCHOR.x, ANCHOR.y, ANCHOR.z);
      camera.add(root);
      dressBone(root, cloneParts(fpArmParts(side, 'upper')), 'cyborg');
      const elbow = new THREE.Group();
      elbow.position.set(0, -ARM_U, 0);
      root.add(elbow);
      dressBone(elbow, cloneParts(fpArmParts(side, 'fore')), 'cyborg');
      const hand = new THREE.Group();
      hand.position.set(0, -ARM_L, 0);
      elbow.add(hand);
      dressBone(hand, cloneParts(fpArmParts(side, 'hand')), 'cyborg');
      const arm = { side, root, elbow, hand, anchor: root.position.clone() };
      root.visible = MODE.view !== 'tp';
      this.arms.push(arm);
      if (side === 1) this.right = arm; else this.left = arm;
    }

    this.mode = null;        // 'pet' | 'shakeDog' | 'shakeNpc'
    this.phase = null;       // 'extend' | 'hold' | 'return'
    this.t = 0;
    this.simT = 0;
    this.cand = null;
    this.target = null;
    this.nextDog = 'pet';    // E on a dog alternates pet <-> paw-shake
    this.contactD = 1;
    this.activeMeet = new THREE.Vector3();
    this._v = new THREE.Vector3();
    this._w = new THREE.Vector3();
    this._goal = new THREE.Vector3();
    this._pad = new THREE.Vector3();
    this._q = new THREE.Quaternion();
    this._qp = new THREE.Quaternion();
    this._idleR = new THREE.Vector3();
    this._idleL = new THREE.Vector3();
    this._sa = new THREE.Vector3();
    this._sb = new THREE.Vector3();

    // Pose both arms NOW so shot mode renders them without ever calling update().
    this._rest(0);

    if (!MODE.shot) {
      this._ui();
      window.addEventListener('keydown', (e) => { if (e.code === 'KeyE') this.tryStart(); });
      if (player.touch) player.touch.onAct = () => this.tryStart();
    }
  }

  _ui() {
    const ret = document.createElement('div');
    ret.id = 'npreticle';
    ret.style.cssText = 'position:fixed;left:50%;top:50%;width:6px;height:6px;margin:-3px 0 0 -3px;'
      + 'border:1px solid rgba(255,255,255,0.55);border-radius:50%;pointer-events:none;z-index:40;';
    document.body.appendChild(ret);
    const cue = document.createElement('div');
    cue.id = 'npcue';
    cue.style.cssText = 'position:fixed;left:50%;top:58%;transform:translateX(-50%);padding:4px 10px;'
      + 'font:13px/1.4 system-ui,sans-serif;color:#cfe8ff;background:rgba(8,12,20,0.55);'
      + 'border:1px solid rgba(120,200,255,0.35);border-radius:4px;pointer-events:none;'
      + 'opacity:0;transition:opacity 120ms;z-index:40;';
    document.body.appendChild(cue);
    this.cue = cue;
  }

  /**
   * Two-bone IK for one arm. `wrist` is a CAMERA-SPACE point; `palm` is the camera-space palm
   * orientation. The ROOT ONLY ROTATES -- its position is never written, which is what keeps the
   * arm growing out of the same corner of the screen no matter where the hand goes.
   */
  _solveArm(arm, wrist, palm) {
    const local = this._v.copy(wrist).sub(arm.root.position);
    const dist = local.length();
    const { root: r, knee } = solveTwoBone(ARM_U, ARM_L, dist);
    const yaw = Math.atan2(local.x, local.z);
    const pitch = Math.atan2(Math.hypot(local.x, local.z), -local.y);
    // k = -1: the elbow folds FORWARD (an arm), the mirror of a knee's backward fold.
    const k = -1;
    arm.root.rotation.set(0, 0, 0);
    arm.root.rotateY(yaw);
    arm.root.rotateX(-(pitch + k * r));
    arm.elbow.rotation.set(k * knee, 0, 0);
    // Palm orientation is absolute in camera space: undo whatever the chain accumulated, so the
    // grip does not spin with the elbow.
    this._q.setFromEuler(palm);
    this._qp.copy(arm.root.quaternion).multiply(arm.elbow.quaternion).invert();
    arm.hand.quaternion.copy(this._qp.multiply(this._q));
  }

  /** Both arms at rest, with a slow breathing drift. */
  _rest(breathe) {
    this._idleR.set(IDLE_AT.x, IDLE_AT.y + breathe, IDLE_AT.z);
    this._idleL.set(-IDLE_AT.x, IDLE_AT.y + breathe * 0.8, IDLE_AT.z);
    this._solveArm(this.right, this._idleR, PALM.idle);
    this._solveArm(this.left, this._idleL, PALM.idle);
  }

  /** The PLAYER's facing, not the camera's -- in third person the lens is behind the body. */
  _fwd(out) {
    return out.set(-Math.sin(this.player.yaw), 0, -Math.cos(this.player.yaw));
  }

  _facing(p) {
    const fwd = this._fwd(this._v);
    const dx = p.x - this.player.pos.x, dz = p.z - this.player.pos.z;
    const d = Math.hypot(dx, dz) || 1;
    return (dx * fwd.x + dz * fwd.z) / d > 0.45;
  }

  /** Nearest interactable within 1.5 m that the player is actually looking at. */
  _candidate() {
    const cp = this.player.pos;
    let best = null, bd = 1.5;
    for (const f of this.fauna) {
      const d = Math.hypot(f.pos.x - cp.x, f.pos.z - cp.z);
      if (d < bd && this._facing(f.pos)) { best = { kind: 'dog', e: f }; bd = d; }
    }
    for (const c of this.cyborgs) {
      const d = Math.hypot(c.pos.x - cp.x, c.pos.z - cp.z);
      if (d < bd && this._facing(c.pos)) { best = { kind: 'npc', e: c }; bd = d; }
    }
    return best;
  }

  tryStart() {
    if (this.mode || !this.cand) return;
    const { kind, e } = this.cand;
    this.target = this.cand;
    this.t = 0;
    this.contactD = 1;
    const fwd = this._fwd(this._v);
    const fl = 1;
    // In third person, everything is measured from the BODY, which is where the arms are.
    const ox = this.body ? this.avatar.pos.x : this.camera.position.x;
    const oy = this.body ? this.avatar.pos.y : this.camera.position.y;
    const oz = this.body ? this.avatar.pos.z : this.camera.position.z;
    if (this.body) this.avatar.interact = { mode: 'shake', t: 0, meet: this.activeMeet };
    if (kind === 'dog') {
      if (this.nextDog === 'pet') {
        this.mode = 'pet';
        e.interact = { mode: 'pet', t: 0 };
        this.count('interact.pet', 1);
      } else {
        this.mode = 'shakeDog';
        // Meet point ahead of the lens, at a natural "give paw" height above the dog's feet --
        // not a high-five the leg geometry cannot reach. Both effectors converge on THIS point.
        this.activeMeet.set(ox + (fwd.x / fl) * 0.60, e.pos.y + 0.45, oz + (fwd.z / fl) * 0.60);
        e.interact = { mode: 'shake', t: 0, meet: this.activeMeet };
        this.count('interact.shakeDog', 1);
      }
    } else {
      this.mode = 'shakeNpc';
      // The clasp happens BETWEEN the two bodies, and the cyborg reaches for it with its own
      // right arm (see Cyborg._aimRightArm). Putting the ideal point here -- rather than letting
      // the player's hand fly all the way to a stationary NPC hand -- is what makes both parties
      // extend, and it keeps the point inside both arms' reach.
      // The clasp sits midway between the two bodies at a natural height -- inside BOTH arms'
      // reach, so each party genuinely extends rather than one lunging at a stationary hand.
      // The clasp is anchored to the two RIGHT SHOULDERS, not to body positions.
      //
      // Two bugs came from using pos.y here. First a units error: a Cyborg's pos.y is the PELVIS,
      // so "+1.24" put the clasp at 2.28 m, above both heads. Then a subtler one -- pos.y is a
      // LERPING value that had not converged when the probe fired, which dropped the clasp 30 cm
      // and made both parties bow 41 degrees to reach it. A shoulder's world position comes from
      // the rig and is true on the frame you ask.
      if (this.body) {
        const sa = this.avatar.arms[this.avatar.rightArm].getWorldPosition(this._sa);
        const sb = e.arms[e.rightArm].getWorldPosition(this._sb);
        this.activeMeet.set((sa.x + sb.x) / 2, Math.min(sa.y, sb.y) - 0.30, (sa.z + sb.z) / 2);
      } else {
        this.activeMeet.set((ox + e.pos.x) / 2, Math.min(oy, e.pos.y) + 0.30, (oz + e.pos.z) / 2);
      }
      e.interact = { mode: 'shake', t: 0, meet: this.activeMeet };
      this.count('interact.shakeNpc', 1);
    }
  }

  /** Where the palm pad must land, in world space. */
  _targetWorld(out) {
    const e = this.target.e;
    if (this.mode === 'pet') return e.petPoint.getWorldPosition(out);
    if (this.mode === 'shakeDog') return out.copy(this.activeMeet);
    // The cyborg's hand is IK-driven toward the meet point but stops at its own reach; the player
    // meets the hand WHERE IT ACTUALLY IS, so the clasp closes at any stand-off distance.
    return e.handTip.getWorldPosition(out);
  }

  _end() {
    if (this.target && this.target.e.interact) this.target.e.interact = null;
    if (this.avatar) this.avatar.interact = null;
    if (this.mode === 'pet' || this.mode === 'shakeDog') this.nextDog = this.nextDog === 'pet' ? 'shake' : 'pet';
    this.mode = null;
    this.phase = null;
    this.target = null;
  }

  update(dt) {
    if (this.frozen) return;   // evidence capture: hold the touch pose on screen
    this.simT += dt;
    this.camera.updateMatrixWorld();
    const breathe = Math.sin(this.simT * 1.7) * 0.010;

    if (!this.mode) {
      this.cand = this._candidate();
      if (this.cue) {
        if (this.cand) {
          const k = this.player.touch ? '' : '[E] ';
          this.cue.textContent = this.cand.kind === 'dog'
            ? (this.nextDog === 'pet' ? `${k}Pet Dog 撫摸` : `${k}Shake Paw 握手`)
            : `${k}Shake Hand 握手`;
          this.cue.style.opacity = '1';
        } else this.cue.style.opacity = '0';
      }
      this._rest(breathe);
      this.phase = null;
      return;
    }

    if (this.cue) this.cue.style.opacity = '0';
    this.t += dt;

    // Walking away cancels; the entity resumes its errand.
    const e = this.target.e;
    if (Math.hypot(e.pos.x - this.player.pos.x, e.pos.z - this.player.pos.z) > 2.3) { this._end(); return; }

    const EXT = 0.55, HOLD = 0.9, RET = 0.45;
    let s;
    if (this.t < EXT) { this.phase = 'extend'; s = ss(this.t / EXT); }
    else if (this.t < EXT + HOLD) { this.phase = 'hold'; s = 1; }
    else if (this.t < EXT + HOLD + RET) { this.phase = 'return'; s = 1 - ss((this.t - EXT - HOLD) / RET); }
    else { this._end(); return; }

    // The left arm keeps resting; only the RIGHT arm reaches.
    this._idleL.set(-IDLE_AT.x, IDLE_AT.y + breathe * 0.8, IDLE_AT.z);
    this._solveArm(this.left, this._idleL, PALM.idle);

    // Wrist goal = (target - pad offset rotated into the palm's camera-space orientation), then
    // eased from the idle wrist point. Interpolating the TARGET rather than the pose means the
    // arm sweeps out from its anchor instead of sliding across the screen.
    const palm = PALM[this.mode];
    const tw = this._targetWorld(this._w);
    this._goal.copy(tw);
    this.camera.worldToLocal(this._goal);
    this._q.setFromEuler(palm);
    this._pad.copy(PAD).applyQuaternion(this._q);
    this._goal.sub(this._pad);
    this._idleR.set(IDLE_AT.x, IDLE_AT.y + breathe, IDLE_AT.z).lerp(this._goal, s);
    this._solveArm(this.right, this._idleR, palm);

    // Instrument: the CONTACT number the gate fences. In third person that is the avatar's own
    // palm against the target -- the FP arms are hidden cosmetics there and measuring them would
    // be measuring something nobody can see.
    if (this.body) {
      // The avatar's arm chases activeMeet; for a pet the target moves with the dog's head, so
      // the shared point is refreshed in place (avatar.interact.meet is the same Vector3).
      if (this.mode === 'pet') this.activeMeet.copy(tw);
      this.avatar.handTip.getWorldPosition(this._pad);
      this.contactD = this._pad.distanceTo(tw);
    } else {
      this._pad.copy(PAD);
      this.right.hand.localToWorld(this._pad);
      this.contactD = this._pad.distanceTo(tw);
    }
  }

  /** Have the arm roots stayed bolted to their anchors? Any drift is the old bug returning. */
  rootDrift() {
    let worst = 0;
    for (const a of this.arms) worst = Math.max(worst, a.root.position.distanceTo(a.anchor));
    return worst;
  }

  /**
   * Synchronous probe for the gate (and for posed screenshots with freezeAtHold). Stands the
   * player 1.15 m in front of the subject, faces it, presses E, and steps the pair with a fixed
   * clock — reporting the MEASURED contact distance, which limb reached, root drift, the paw's
   * distance to the meet point, the dog's head response, and every event that fired.
   */
  debugRun(kind, opts = {}) {
    const dog = kind !== 'shakeNpc';
    const e = dog ? this.fauna[0] : (this.cyborgs.find((c) => c.mode === 'idle') || this.cyborgs[0]);
    const fx = Math.sin(e.heading), fz = Math.cos(e.heading);
    this.player.pos.set(e.pos.x + fx * 1.15, e.pos.y, e.pos.z + fz * 1.15);
    if (this.player.vel) this.player.vel.set(0, 0, 0);
    this.player.yaw = Math.atan2(-(e.pos.x - this.player.pos.x), -(e.pos.z - this.player.pos.z));
    // Evidence framing: the chase camera sits square behind the body, so a head-on approach hides
    // the subject behind the player. Standing off-axis puts both bodies and the clasp in frame --
    // and it is still a legal interaction (the facing test allows up to ~63 degrees).
    if (opts.yawOffset) this.player.yaw += opts.yawOffset;
    // Look where the clasp actually happens -- an evidence still that crops the contact out of
    // frame proves nothing.
    this.player.pitch = kind === 'shakeDog' ? -0.55 : kind === 'shakeNpc' ? -0.26 : -0.34;
    this.nextDog = kind === 'pet' ? 'pet' : 'shake';
    this.mode = null;
    this.frozen = false;

    const evs = {};
    const countReal = this.count;
    this.count = (k, n) => { evs[k] = (evs[k] || 0) + n; countReal(k, n); };
    const step = 1 / 60;
    // SETTLE. The avatar's pelvis height is a lerp toward its stance; teleport it and read the
    // value on the same frame and you get a number that is still travelling. Everything that
    // hangs off body height -- the clasp point, the lean, the crouch -- inherits that error.
    for (let i = 0; i < 40; i++) {
      this.player.update(step);
      if (this.avatar) this.avatar.drive(step, this.player.pos, this.player.yaw + Math.PI, 0);
    }
    this.update(step);            // refresh candidate with the camera settled
    // Pin the subject. _candidate() picks the NEAREST entity, and on a live page another dog can
    // drift closer than the teleported subject -- the interaction then lands on the wrong animal
    // while every measurement reads this one.
    this.cand = { kind: dog ? 'dog' : 'npc', e };
    this.tryStart();
    const started = !!this.mode;
    let minContact = Infinity, minPaw = Infinity, headLift = 0, drift = 0;
    const baseHead = dog ? e.head.rotation.x : 0;
    const tmp = new THREE.Vector3();
    if (started) {
      for (let i = 0; i < 170 && this.mode; i++) {
        this.player.update(step);
        if (this.avatar) this.avatar.drive(step, this.player.pos, this.player.yaw + Math.PI, 0);
        const ev = e.update(step, this.player.pos);
        for (const k in ev) this.count(k, ev[k]);
        this.update(step);
        drift = Math.max(drift, this.rootDrift());
        if (this.phase === 'hold') {
          minContact = Math.min(minContact, this.contactD);
          if (kind === 'shakeDog') {
            e.root.updateMatrixWorld(true);
            minPaw = Math.min(minPaw, e.legs[e.shakeLeg].foot.getWorldPosition(tmp).distanceTo(this.activeMeet));
          }
          if (kind === 'pet') headLift = Math.max(headLift, baseHead - e.head.rotation.x);
          if (opts.freezeAtHold && this.t > 0.55 + 0.45) { this.frozen = true; break; }
        }
      }
    }
    this.count = countReal;

    // WHICH limb reached, measured in the subject's own body frame rather than restated from the
    // index that drove it: right is -X for a body facing +Z (z cross y = -x).
    let side = null;
    if (started) {
      if (kind === 'shakeNpc') {
        e.root.updateMatrixWorld(true);
        const lp = e.root.worldToLocal(e.handTip.getWorldPosition(tmp).clone());
        side = { arm: e.rightArm, localX: +lp.x.toFixed(3), shoulderX: +e.arms[e.rightArm].position.x.toFixed(3) };
      } else if (kind === 'shakeDog') {
        const hp = e.legs[e.shakeLeg].spec.hip;
        side = { leg: e.shakeLeg, hipX: +hp[0].toFixed(3), hipZ: +hp[2].toFixed(3) };
      }
    }
    // In third person the PLAYER also has a side to prove: its own reaching arm must be the
    // anatomical right one, measured in the avatar's body frame exactly as the NPC's is.
    let playerSide = null;
    if (started && this.body) {
      this.avatar.root.updateMatrixWorld(true);
      const lp = this.avatar.root.worldToLocal(this.avatar.handTip.getWorldPosition(tmp).clone());
      playerSide = {
        arm: this.avatar.rightArm,
        shoulderX: +this.avatar.arms[this.avatar.rightArm].position.x.toFixed(3),
        localX: +lp.x.toFixed(3),
      };
    }
    return {
      started, side, playerSide, view: this.tp ? 'tp' : 'fp', body: this.body,
      contact: minContact === Infinity ? null : +minContact.toFixed(3),
      paw: minPaw === Infinity ? null : +minPaw.toFixed(3),
      headLift: +headLift.toFixed(3),
      rootDrift: +drift.toFixed(4),
      frozen: !!opts.freezeAtHold && this.mode !== null,
      evs,
    };
  }
}
